import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePostRegisterMutation } from "@/store/service";
import { FieldValues, useForm } from "react-hook-form";
import { getEmailValidationPattern } from "@/util/validation";
import RegisterData from "@/model/interface/RegisterData";
import { passwordRules } from "@/model/schemas";
import { ApiErrorResponse } from "@/store/axiosBaseQuery";
import { UserPlus, ArrowRight, Loader2 } from "lucide-react";

const inputClass =
  "w-full h-11 rounded-lg border border-gray-300 bg-white px-4 text-sm outline-none transition-colors focus:border-[#0FD131] focus:ring-2 focus:ring-[#9BFBE0]";

const Register = () => {
  const { isAuthenticated, authHydrated, refreshAuthStatus } = useAuth();
  const navigate = useNavigate();
  const [postRegister, postRegisterResult] = usePostRegisterMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (userData: FieldValues) => {
    setErrorMessage(null);
    try {
      await postRegister(userData as RegisterData).unwrap();
      await refreshAuthStatus();
    } catch (error) {
      const detail = (error as Partial<ApiErrorResponse>).detail;
      setErrorMessage(detail || "We couldn't create your account. Please try again.");
    }
  };

  useEffect(() => {
    if (authHydrated && isAuthenticated) {
      navigate("/dashboard");
    }
  }, [authHydrated, isAuthenticated]);

  if (!authHydrated) {
    return null;
  }

  return (
    <div className="flex flex-1 items-center justify-center py-16 px-4">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <>
              <div className="flex flex-col items-center text-center mb-8">
                <div className="flex items-center justify-center size-14 rounded-full bg-[#9BFBE0]/30 mb-4">
                  <UserPlus className="size-6 text-[#0FD131]" />
                </div>
                <h1 className="text-2xl font-semibold text-gray-900">Create an account</h1>
                <p className="mt-2 text-sm text-gray-500">Get started with OnHome today</p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First name</label>
                    <input
                      type="text"
                      {...register("firstName", {
                        required: true,
                        minLength: 2,
                        maxLength: 50,
                      })}
                      className={`${inputClass} ${errors.firstName ? "border-red-400" : ""}`}
                      placeholder="John"
                    />
                    {errors.firstName && <p className="text-xs text-red-500 mt-1">First name is required</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last name</label>
                    <input
                      type="text"
                      {...register("lastName", {
                        required: true,
                        minLength: 2,
                        maxLength: 50,
                      })}
                      className={`${inputClass} ${errors.lastName ? "border-red-400" : ""}`}
                      placeholder="Doe"
                    />
                    {errors.lastName && <p className="text-xs text-red-500 mt-1">Last name is required</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    {...register("email", {
                      required: true,
                      pattern: getEmailValidationPattern(),
                    })}
                    className={`${inputClass} ${errors.email ? "border-red-400" : ""}`}
                    placeholder="john@example.com"
                  />
                  {errors.email && <p className="text-xs text-red-500 mt-1">A valid email is required</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    {...register("password", {
                      required: true,
                      validate: (value: string) => passwordRules.every((rule) => rule.isMet(value)),
                    })}
                    className={`${inputClass} ${errors.password ? "border-red-400" : ""}`}
                    placeholder="Create a password"
                  />
                  {errors.password && (
                    <p className="text-xs text-red-500 mt-1">
                      Use at least 8 characters with an uppercase letter, a lowercase letter, a number and a symbol
                    </p>
                  )}
                </div>

                {errorMessage && (
                  <p role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                    {errorMessage}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={postRegisterResult.isLoading}
                  className="flex items-center justify-center gap-2 w-full h-12 rounded-full bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {postRegisterResult.isLoading ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <>
                      Create account
                      <ArrowRight className="size-4" />
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-gray-500">
                  Already have an account?{" "}
                  <button
                    onClick={() => navigate("/login")}
                    className="font-medium text-[#0FD131] hover:underline cursor-pointer"
                  >
                    Sign in
                  </button>
                </p>
              </div>
            </>
        </div>
      </div>
    </div>
  );
};

export default Register;
